/**
 * Public-identifier administration: the blacklist page and the renames (TASK-0060).
 *
 * Every mutation invalidates the `['admin', 'public-identifiers']` prefix rather than patching an entry by hand: adding a
 * reservation can change what the next page holds, and releasing one removes a row from a page already in the cache — a
 * second model of that rule in the client is exactly how a stale list appears.
 *
 * A rename also invalidates the account lookup, because the handle an administrator searched by may now belong to a
 * different account.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { adminPublicIdentifierApi } from '@/features/admin/api/adminPublicIdentifierApi';
import { queryKeys } from '@/lib/queryKeys';

/** The prefix a blacklist mutation invalidates. */
const BLACKLIST_PREFIX = ['admin', 'public-identifiers'] as const;

/** The prefix a rename invalidates, so a stale lookup cannot answer with the old handle. */
const USER_LOOKUP_PREFIX = ['admin', 'users', 'by-public-identifier'] as const;

/**
 * Reads one page of the blacklist.
 *
 * @param page - One-based page number.
 * @param pageSize - Rows per page; the server clamps it.
 * @returns Query result carrying the page.
 */
export function usePublicIdentifierBlacklist(page = 1, pageSize = 20) {
  return useQuery({
    queryKey: queryKeys.adminPublicIdentifierBlacklist(page, pageSize),
    queryFn: () => adminPublicIdentifierApi.listBlacklist(page, pageSize),
  });
}

/**
 * Looks up the account that owns a handle.
 *
 * @param publicIdentifier - Handle to look up; the query is skipped while it is empty.
 * @returns Query result carrying the account, or a `404` problem when no account holds it.
 */
export function useUserByPublicIdentifier(publicIdentifier: string) {
  return useQuery({
    queryKey: queryKeys.adminUserByPublicIdentifier(publicIdentifier),
    enabled: publicIdentifier.trim().length > 0,
    // A missing account is an answer, not a failure to retry: the administrator typed a handle that is free.
    retry: false,
    queryFn: () => adminPublicIdentifierApi.findUser(publicIdentifier.trim()),
  });
}

/** Renames the handle of one account. */
export function useChangeUserPublicIdentifier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, publicIdentifier }: { userId: string; publicIdentifier: string }) =>
      adminPublicIdentifierApi.changeUserIdentifier(userId, publicIdentifier),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: USER_LOOKUP_PREFIX });
    },
  });
}

/** Reserves a handle for the platform. */
export function useBlacklistPublicIdentifier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ publicIdentifier, note }: { publicIdentifier: string; note?: string | null }) =>
      adminPublicIdentifierApi.blacklist(publicIdentifier, note),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: BLACKLIST_PREFIX });
    },
  });
}

/** Releases a reservation. */
export function useReleasePublicIdentifier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (entryId: string) => adminPublicIdentifierApi.release(entryId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: BLACKLIST_PREFIX });
    },
  });
}
